import { useState, useEffect, useCallback } from 'react';
import { recurringAppointmentsAPI, patientsAPI, doctorAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Repeat, X, Calendar } from 'lucide-react';
import TouchTimePicker from '../components/TouchTimePicker';

const RECURRENCE = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const RecurringAppointmentsPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [series, setSeries] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const res = await recurringAppointmentsAPI.list(params);
      setSeries(res.data || []);
    } catch { toast.error('Failed to load'); }
  }, [hospitalId]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!confirm('Cancel entire recurring series? All future appointments will be cancelled.')) return;
    try { await recurringAppointmentsAPI.cancel(id); toast.success('Series cancelled'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="recurring-appointments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Recurring Appointments</h1>
          <p className="text-sm text-slate-500 mt-0.5">{series.length} series · auto-generates appointment instances</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-recurring-btn">
          <Plus size={16} className="mr-1.5" /> New Series
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Patient</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Doctor</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Pattern</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Time</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Start Date</th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Occurrences</th>
              <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {series.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">No recurring appointments yet.</td></tr>
            ) : series.map(s => (
              <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`series-${s.id}`}>
                <td className="py-3 px-4 font-medium text-slate-800">{s.patient_name}</td>
                <td className="py-3 px-4 text-slate-600">{s.doctor_name}</td>
                <td className="py-3 px-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 capitalize flex items-center gap-1 w-fit">
                    <Repeat size={10} /> {s.recurrence}
                  </span>
                </td>
                <td className="py-3 px-4">{s.time}</td>
                <td className="py-3 px-4 text-slate-600">{s.start_date}</td>
                <td className="py-3 px-4 text-center">{s.total_occurrences}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`text-xs px-2 py-1 rounded-full ${s.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {s.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  {s.status === 'active' && (
                    <button onClick={() => cancel(s.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`cancel-${s.id}`}>
                      <X size={14} className="text-red-400" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} hospitalId={hospitalId} onSaved={load} />
    </div>
  );
};

const Modal = ({ open, onClose, hospitalId, onSaved }) => {
  const [form, setForm] = useState({ patient_id: '', doctor_id: '', recurrence: 'weekly', time: '10:00', start_date: '', total_occurrences: 4, consultation_type: 'Follow-up' });
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    patientsAPI.list({ limit: 200 }).then(r => setPatients(r.data || [])).catch(() => {});
    doctorAPI.list().then(r => setDoctors(r.data || [])).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!form.patient_id || !form.doctor_id || !form.start_date) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      const patient = patients.find(p => p.id === form.patient_id);
      const doctor = doctors.find(d => d.id === form.doctor_id);
      await recurringAppointmentsAPI.create({
        ...form,
        hospital_id: hospitalId,
        patient_name: patient ? `${patient.first_name} ${patient.last_name}` : '',
        doctor_name: doctor ? `${doctor.first_name} ${doctor.last_name}` : '',
        total_occurrences: parseInt(form.total_occurrences) || 4,
      });
      toast.success('Series created');
      onSaved(); onClose();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Recurring Series</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Patient *</Label>
            <Select value={form.patient_id} onValueChange={v => setForm({ ...form, patient_id: v })}>
              <SelectTrigger data-testid="recurring-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>{patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Doctor *</Label>
            <Select value={form.doctor_id} onValueChange={v => setForm({ ...form, doctor_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select doctor" /></SelectTrigger>
              <SelectContent>{doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Recurrence</Label>
              <Select value={form.recurrence} onValueChange={v => setForm({ ...form, recurrence: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{RECURRENCE.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Total Occurrences</Label><Input type="number" value={form.total_occurrences} onChange={e => setForm({ ...form, total_occurrences: e.target.value })} /></div>
            <div><Label>Start Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-start-date" /></div>
            <div><Label>Time</Label><TouchTimePicker value={form.time} onChange={(v) => setForm({ ...form, time: v })} testId="recur-time" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-recurring-btn">{saving ? 'Creating...' : 'Create Series'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringAppointmentsPage;
